import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, ViewChild, Inject, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';

import { GoogleMap } from '@angular/google-maps';
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

import { SpinnerService } from '../../services/spinner.service';
import { ApiFloor, ApiSetFloorsRequest, ApiFile, SettingFile } from '../../api-client-module';
import { Exhibit } from '../../firestore-models/exhibit';
import { Hall } from '../../firestore-models/hall';
import { UploadedFile } from '../../firestore-models/uploaded-file';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { MultiFloorDialogComponent } from '../../common/multi-floor-dialog/multi-floor-dialog.component';
import { MultiNameDialogComponent } from '../../common/multi-name-dialog/multi-name-dialog.component';
import { MultiDescDialogComponent } from '../../common/multi-desc-dialog/multi-desc-dialog.component';
import { MultiExternallinkDialogComponent } from '../../common/multi-externallink-dialog/multi-externallink-dialog.component';
import { SelectUploadedFileDialogComponent } from '../../select-uploaded-file-dialog/select-uploaded-file-dialog.component';
import { SelectExhibitsDialogComponent } from '../select-exhibits-dialog/select-exhibits-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface FloorDialogData {
  change_type: number;
  floor?: ApiFloor;
}

export interface marker {
  lat: number;
  lng: number;
  draggable: boolean;
}

@Component({
  selector: 'app-floor-dialog',
  templateUrl: './floor-dialog.component.html',
  styleUrls: ['./floor-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class FloorDialogComponent implements OnInit {

  //フロア情報
  floor?: ApiFloor;
  //各フロア情報に紐付くプロジェクトID
  projectId: string;

  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;

  //フロア情報フォーム
  floorFormGroup!: FormGroup;
  floorIdControl!: FormControl;
  floorExplainControl!: FormControl;
  hallIdControl!: FormControl;
  autoTransControl!: FormControl;
  floorNamesControl!: FormControl;
  floorDescsControl!: FormControl;
  floorLatControl!: FormControl;
  floorLngControl!: FormControl;
  showMarkerControl!: FormControl;
  externallinkNamesControl!: FormControl;
  externallinkUrlsControl!: FormControl;
  useExternallinkControl!: FormControl;
  eventStartDateControl!: FormControl;
  eventEndDateControl!: FormControl;
  floorFileExpandControl!: FormControl;

  private hallsCollection!: AngularFirestoreCollection<Hall>;
  halls!: Observable<Hall[]>;

  floorFiles: SettingFile[] = [];
  exhibits: Exhibit[] = [];

  //GoogleMap表示
  zoom = AppConst.ZOOM_INITIAL_VALUE;
  center!: google.maps.LatLngLiteral;
  options: google.maps.MapOptions = {
    disableDefaultUI: true
  };
  currentPosition!: google.maps.LatLngLiteral
  currentPositionMarkerOption: google.maps.MarkerOptions = {
    draggable: true
  };

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild("mapSearchField") searchField!: ElementRef;
  @ViewChild("mapSearchFieldExecute") searchFieldExecute!: ElementRef;
  @ViewChild("mapSearchFieldClear") searchFieldClear!: ElementRef;
  searchBox!: google.maps.places.SearchBox;

  constructor(
    public dialogRef: MatDialogRef<FloorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FloorDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.hallsCollection = this.angularFirestore.collection<Hall>(
      AppConst.FIRESTORE_HALLS, ref => ref.where('project_id', '==', this.projectId));
    this.halls = this.hallsCollection.valueChanges({ idField: 'id' });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });

    this.floor = this.data.floor;
    if (!this.floor) {
      this.floor = {
        floor_id: '',
        floor_explain: '',
        hall_id: '',
        floor_names: [],
        floor_descs: null as any,
        latitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.latitude ? this.configurationService.getProject().project_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
        longitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.longitude ? this.configurationService.getProject().project_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
        show_marker: false,
        externallink_names: [],
        externallink_urls: [],
        use_externallink: false,
        floor_files: [],
        floor_file_expand: false,
        exhibit_ids: [],
        event_start_date: null as any,
        event_end_date: null as any,
        project_id: this.projectId,
        enabled: true,
      };
      this.isAdded = true;
    } else {
      if (!this.floor.latitude) {
        this.floor.latitude = AppConst.LATITUDE_INITIAL_VALUE;
      }
      if (!this.floor.longitude) {
        this.floor.longitude = AppConst.LONGITUDE_INITIAL_VALUE;
      }
    }
    this.currentPosition = {
      lat: this.floor.latitude!,
      lng: this.floor.longitude!
    }
    this.center = this.currentPosition;

    this.createForm();
    this.floorIdControl = this.floorFormGroup.get('floorId') as FormControl;
    this.floorExplainControl = this.floorFormGroup.get('floorExplain') as FormControl;
    this.hallIdControl = this.floorFormGroup.get('hallId') as FormControl;
    this.autoTransControl = this.floorFormGroup.get('autoTrans') as FormControl;
    this.floorNamesControl = this.floorFormGroup.get('floorNames') as FormControl;
    this.floorDescsControl = this.floorFormGroup.get('floorDescs') as FormControl;
    this.floorLatControl = this.floorFormGroup.get('floorLat') as FormControl;
    this.floorLngControl = this.floorFormGroup.get('floorLng') as FormControl;
    this.showMarkerControl = this.floorFormGroup.get('showMarker') as FormControl;
    this.externallinkNamesControl = this.floorFormGroup.get('externallinkNames') as FormControl;
    this.externallinkUrlsControl = this.floorFormGroup.get('externallinkUrls') as FormControl;
    this.useExternallinkControl = this.floorFormGroup.get('useExternallink') as FormControl;
    this.eventStartDateControl = this.floorFormGroup.get('eventStartDate') as FormControl;
    this.eventEndDateControl = this.floorFormGroup.get('eventEndDate') as FormControl;
    this.floorFileExpandControl = this.floorFormGroup.get('floorFileExpand') as FormControl;

    this.getFiles();
    this.getExhibits();
  }

  ngAfterViewInit(): void {
    // 住所検索用
    this.searchBox = new google.maps.places.SearchBox(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldExecute.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldClear.nativeElement
    );
  }

  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }

  //GoogleMap上のピンとフォームの値を一致させる
  onMerkerDragEnd(event: any) {
    this.floorLatControl.setValue(this.round(event.latLng!.lat(), 8));
    this.floorLngControl.setValue(this.round(event.latLng!.lng(), 8));
  }

  //ピンの位置
  private round(value: number, digits: number): number {
    let pow: number = 1;
    if (digits > 0) {
      pow = Math.pow(10, digits);
    }
    return Math.round(value * pow) / pow;
  }

  //Googleマップと緯度のフォーム連携
  onBlurMarkerLat() {
    if (!!this.floorLatControl.valid) {
      this.floorLatControl.setValue(this.round(this.floorLatControl.value, 8));
      this.currentPosition = {
        lat: this.round(this.floorLatControl.value, 8),
        lng: this.currentPosition.lng
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //Googleマップと経度のフォーム連携
  onBlurMarkerLng() {
    if (!!this.floorLngControl.valid) {
      this.floorLngControl.setValue(this.round(this.floorLngControl.value, 8));
      this.currentPosition = {
        lat: this.currentPosition.lat,
        lng: this.round(this.floorLngControl.value, 8)
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //フロア画像情報並べ替え
  onDropFile(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.floorFiles, event.previousIndex, event.currentIndex);
    let files: Array<ApiFile> = [];
    for (const file of this.floorFiles) {
      files.push({ file_id: file.id, lang_code: file.lang_code });
    }
    this.floor!.floor_files = files;
  }

  //フロア情報並べ替え
  onDropExhibit(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.exhibits, event.previousIndex, event.currentIndex);
    let exhibitLists: Array<string> = [];
    for (const exhibit of this.exhibits) {
      exhibitLists.push(exhibit.id!);
    }
    this.floor!.exhibit_ids = exhibitLists;
  }

  //以下、フォームの記入エラー表示
  public getErrorMessageToFloorId() {
    return this.floorIdControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.floorIdControl.hasError('pattern')
          ? this.translate.instant('半角英数字ハイフンのみ')
          : '';
  }

  public getErrorMessageToHallId() {
    return this.hallIdControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToFloorDescs() {
    return this.floorDescsControl.hasError('required')
      ? this.translate.instant('必須項目')
      : '';
  }

  public getErrorMessageToFloorLat() {
    return this.floorLatControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.floorLatControl.hasError('pattern') || this.floorLatControl.hasError('min') || this.floorLatControl.hasError('max')
          ? this.translate.instant('-90から90の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToFloorLng() {
    return this.floorLngControl.hasError('required')
      ? this.translate.instant('必須項目')
      : this.floorLngControl.hasError('pattern') || this.floorLngControl.hasError('min') || this.floorLngControl.hasError('max')
          ? this.translate.instant('-180から180の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToEventEndDate() {
    return this.translate.instant('開催開始日より前の日は設定不可');
  }
  //フォームの値を各要素に代入
  private createForm() {
    this.floorFormGroup = this.formBuilder.group(
      {
        floorId: [this.floor!.floor_id, [Validators.required, Validators.pattern('^[a-zA-Z0-9!-/:-@¥[-`{-~]*$')]],
        floorExplain: [this.floor!.floor_explain],
        hallId: [this.floor!.hall_id, [Validators.required]],
        floorNames: [this.floor!.floor_names],
        floorDescs: [this.floor!.floor_descs, [Validators.required]],
        floorLat: [this.floor!.latitude, [Validators.required, Validators.min(-90), Validators.max(90), Validators.pattern('^(\-)?([1-9][0-9]{0,1}|0)(\.[0-9]{1,8})?$')]],
        floorLng: [this.floor!.longitude, [Validators.required, Validators.min(-180), Validators.max(180), Validators.pattern('^(\-)?([1-9][0-9]{0,2}|0)(\.[0-9]{1,8})?$')]],
        showMarker: [this.floor!.show_marker, []],
        externallinkNames: [this.floor!.externallink_names, []],
        externallinkUrls: [this.floor!.externallink_urls, []],
        useExternallink: [this.floor!.use_externallink, []],
        eventStartDate: [this.floor!.event_start_date],
        eventEndDate: [this.floor!.event_end_date],
        floorFileExpand: [this.floor!.floor_file_expand, []],
      }, {
      validator: this.isValidEventDate
    }
    );
  }

  //フロア画像ファイル設定
  private getFiles() {
    this.floorFiles = [];
    if (!!this.floor!.floor_files && this.floor!.floor_files.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトのアップロードファイルを全て取得してから
      //       フロア情報に登録されている id で絞り込む。
      this.angularFirestore.collection<UploadedFile>(
        AppConst.FIRESTORE_UPLOADED_FILES, ref => ref.where('project_id', '==', this.projectId)
      ).valueChanges({ idField: 'id' }).subscribe(docs => {
        this.floorFiles = [];
        if (!!docs) {
          for (const floorFile of this.floor!.floor_files!) {
            for (const uploadedFile of docs) {
              if (floorFile.file_id === uploadedFile.id) {
                const settingFile: SettingFile = {
                  id: uploadedFile.id,
                  file_name: uploadedFile.file_name,
                  download_url: uploadedFile.download_url,
                  lang_code: floorFile.lang_code,
                }
                this.floorFiles.push(settingFile);
                break;
              }
            }
          }
        }
      });
    }
  }

  // 変更された言語を登録
  changeFloorFileLang(i: number, event: any) {
    this.floor!.floor_files![i].lang_code = event.value;
    this.floorFiles![i].lang_code = event.value;
  }

  //フロアに紐付く展示情報設定
  private getExhibits() {
    this.exhibits = [];
    if (!!this.floor!.exhibit_ids && this.floor!.exhibit_ids.length > 0) {
      // NOTE: in で取得できるのは最大10までのため、該当プロジェクトの展示情報を全て取得してから
      //       フロア情報に登録されている id で絞り込む。
      this.angularFirestore.collection<Exhibit>(
        AppConst.FIRESTORE_EXHIBITS, ref => ref.where('project_id', '==', this.projectId)
      ).valueChanges({ idField: 'id' }).subscribe(docs => {
        this.exhibits = [];
        if (!!docs) {
          for (const exhibitId of this.floor!.exhibit_ids!) {
            for (const exhibit of docs) {
              if (exhibitId === exhibit.id) {
                this.exhibits.push(exhibit);
                break;
              }
            }
          }
        }
      });
    }
  }

  //更新ボタン
  addFloor(): void {
    if (this.floorFormGroup.valid) {
      this.floor!.floor_id = this.floorIdControl.value;
      this.floor!.floor_explain = this.floorExplainControl.value;
      this.floor!.hall_id = this.hallIdControl.value;
      this.floor!.floor_names = this.floorNamesControl.value;
      this.floor!.floor_descs = this.floorDescsControl.value;
      this.floor!.latitude = this.floorLatControl.value;
      this.floor!.longitude = this.floorLngControl.value;
      this.floor!.show_marker = this.showMarkerControl.value;
      this.floor!.externallink_names = this.externallinkNamesControl.value;
      this.floor!.externallink_urls = this.externallinkUrlsControl.value;
      this.floor!.use_externallink = this.useExternallinkControl.value;
      this.floor!.event_start_date = this.eventStartDateControl.value ? this.eventStartDateControl.value : null;
      this.floor!.event_end_date = this.eventEndDateControl.value ? this.eventEndDateControl.value : null;
      this.floor!.floor_file_expand = this.floorFileExpandControl.value;
      if (this.floor) {
        let request: ApiSetFloorsRequest = {
          access_token: '',
          change_type: this.data.change_type,
          floors: [this.floor]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setFloors(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'))
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 })
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  //フロア名称ダイアログ
  openFloorNameDialog(): void {
    const dialogRef = this.dialog.open(MultiFloorDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.floor!.floor_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.floor!.floor_names = result;
        this.floorNamesControl.setValue(this.floor!.floor_names);
      }
    });
  }

  //フロア詳細ダイアログ
  openFloorDescDialog(): void {
    const dialogRef = this.dialog.open(MultiDescDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { descs: this.floor!.floor_descs }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.floor!.floor_descs = result;
        this.floorDescsControl.setValue(this.floor!.floor_descs);
      }
    });
  }

  //フロア画像ファイル選択ダイアログ
  openSelectFilesDialog(): void {
    const dialogRef = this.dialog.open(SelectUploadedFileDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { uploadedFile: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        for (const row of result) {
          const lang = this.projectLanguages[0].lang_code;
          this.floor!.floor_files!.push({ file_id: row.id, lang_code: lang });
        }
        this.getFiles();
      }
    });
  }

  //外部リンク名称ダイアログ
  openExternallinkNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.floor!.externallink_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.floor!.externallink_names = result;
        this.externallinkNamesControl.setValue(this.floor!.externallink_names);
      }
    });
  }

  //外部リンクＵＲＬダイアログ
  openExternallinkUrlDialog(): void {
    const dialogRef = this.dialog.open(MultiExternallinkDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { codes: this.floor!.externallink_urls }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.floor!.externallink_urls = result;
        this.externallinkUrlsControl.setValue(this.floor!.externallink_urls);
      }
    });
  }

  deleteFiles(i: number): void {
    this.floor!.floor_files!.splice(i, 1);
    this.getFiles();
  }

  //展示選択ダイアログ
  openSelectExhibitsDialog(): void {
    const dialogRef = this.dialog.open(SelectExhibitsDialogComponent, {
      width: '80%',
      height: '90%',
      disableClose: true,
      data: { exhibit: null }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result && result.length > 0) {
        if (!this.floor!.exhibit_ids) {
          this.floor!.exhibit_ids = new Array<string>();
        }
        let isChanged: boolean = false;
        for (const row of result) {
          if (!this.floor!.exhibit_ids.includes(row.id)) {
            this.floor!.exhibit_ids.push(row.id);
            isChanged = true;
          }
        }
        if (!!isChanged) {
          this.getExhibits();
        }
      }
    });
  }

  //展示リストの削除
  deleteExhibit(id: string): void {
    if (!!this.floor!.exhibit_ids!.includes(id)) {
      this.floor!.exhibit_ids = this.floor!.exhibit_ids!.filter(value => value !== id);
      this.getExhibits();
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // マップ検索用のキー制御
  onKeyDownFromSearchPlace(keyCode: number): boolean {
    if (keyCode && keyCode === 13) {
      // NOTE: Enterを叩かれると他の項目の編集ボタンが反応してしまうのを防ぐ
      return false;
    }
    return true;
  }

  // マップ検索
  searchMap(): void {
    const places = this.searchBox.getPlaces();
    if (places!.length === 0) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    places!.forEach(place => {
      if (!place.geometry || !place.geometry.location) {
        return;
      }
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    // console.log(bounds.getCenter().lat());
    // this.map.fitBounds(bounds);
    this.floorLatControl.setValue(this.round(bounds.getCenter().lat(), 8));
    this.floorLngControl.setValue(this.round(bounds.getCenter().lng(), 8));
    this.currentPosition = {
      lat: this.round(bounds.getCenter().lat(), 8),
      lng: this.round(bounds.getCenter().lng(), 8)
    }
    this.map.panTo(this.currentPosition);
  }

  // マップ検索フィールドクリア
  clearMapSearchField(): void {
    this.searchField.nativeElement.value = "";
  }

  // 開催期間整合制チェック
  isValidEventDate(ac: AbstractControl) {
    const startDate = ac.get('eventStartDate');
    const endDate = ac.get('eventEndDate');
    endDate?.setErrors(null);
    if (!!startDate && !!endDate && !!startDate.value && !!endDate.value) {
      if (startDate.value > endDate.value) {
        endDate.setErrors({ before_start_date: true });
      }
    }
  }

  clearEventStartDate(): void {
    this.eventStartDateControl.setValue(null);
  }

  clearEventEndDate(): void {
    this.eventEndDateControl.setValue(null);
  }
}
